import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, addDays, subDays } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import axios from 'axios';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  CATEGORY_COLORS,
  STATUS_COLORS,
  generateScheduledEvents,
  mapHistoryToEvents,
  getEventStyle,
  shortenAutomationName,
  collapseFrequentEvents,
} from './calendarUtils';

const locales = { 'en-US': enUS };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

/**
 * Custom toolbar for the calendar using MUI components.
 */
const CalendarToolbar = ({ label, onNavigate, onView, view }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      mb: 1.5,
      px: 0.5,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Button
        size="small"
        variant="outlined"
        onClick={() => onNavigate('TODAY')}
        startIcon={<TodayIcon sx={{ fontSize: 16 }} />}
        sx={{ textTransform: 'none', fontFamily: 'Lato', fontSize: 12, py: 0.25 }}
      >
        Today
      </Button>
      <IconButton size="small" onClick={() => onNavigate('PREV')}>
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={() => onNavigate('NEXT')}>
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>

    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: 'Lato' }}>
      {label}
    </Typography>

    <ButtonGroup size="small" variant="outlined">
      {['month', 'week', 'day'].map((v) => (
        <Button
          key={v}
          onClick={() => onView(v)}
          variant={view === v ? 'contained' : 'outlined'}
          sx={{ textTransform: 'capitalize', fontFamily: 'Lato', fontSize: 12, py: 0.25 }}
        >
          {v}
        </Button>
      ))}
    </ButtonGroup>
  </Box>
);

/**
 * Custom event component showing name + category-colored dot.
 */
const CalendarEvent = ({ event }) => {
  const categoryColor = CATEGORY_COLORS[event.categoryKey] || CATEGORY_COLORS.other;
  const displayName = shortenAutomationName(event.title);
  const timeStr = format(event.start, 'p');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
      <FiberManualRecordIcon sx={{ fontSize: 8, color: categoryColor.main, flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <span style={{ opacity: 0.7, marginRight: 4 }}>{timeStr}</span>
        {displayName}
        {event.collapsed && (
          <span style={{ opacity: 0.6, marginLeft: 4 }}>({event.collapsedCount}x)</span>
        )}
      </span>
    </Box>
  );
};

/**
 * Legend showing category + status color meanings.
 */
const CalendarLegend = () => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5, px: 0.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
        Category:
      </Typography>
      {Object.entries(CATEGORY_COLORS).map(([key, colorObj]) => (
        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Box sx={{ width: 12, height: 3, backgroundColor: colorObj.main, borderRadius: 1 }} />
          <Typography variant="caption" sx={{ textTransform: 'capitalize', color: 'text.secondary' }}>
            {key}
          </Typography>
        </Box>
      ))}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
        Status:
      </Typography>
      {Object.entries(STATUS_COLORS).map(([key, val]) => (
        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <FiberManualRecordIcon sx={{ fontSize: 8, color: val.dot }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{val.label}</Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const AutomationsCalendar = ({ automations, firebaseUser, selectedCategory, platformFilter, nameFilter }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('day');
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Build automations map for history lookup
  const automationsMap = useMemo(() => {
    const map = {};
    automations.forEach((a) => { map[a.id] = a; });
    return map;
  }, [automations]);

  // Compute visible date range based on current view
  const visibleRange = useMemo(() => {
    const now = currentDate;
    if (view === 'month') {
      // Pad by a week on each side for partial weeks
      const start = subDays(startOfMonth(now), 7);
      const end = addDays(endOfMonth(now), 7);
      return { start, end };
    }
    if (view === 'week') {
      const start = subDays(startOfWeek(now, { locale: enUS }), 1);
      const end = addDays(start, 9);
      return { start, end };
    }
    // day
    const start = subDays(now, 1);
    const end = addDays(now, 2);
    return { start, end };
  }, [currentDate, view]);

  // Filter automations the same way the list view does
  const filteredAutomations = useMemo(() => {
    let list = automations;
    if (selectedCategory) {
      list = list.filter((a) => (a.categoryKey || 'other') === selectedCategory);
    }
    if (nameFilter) {
      list = list.filter((a) => a.name.toLowerCase().includes(nameFilter.toLowerCase()));
    }
    if (platformFilter) {
      list = list.filter((a) => {
        if (platformFilter === 'snowflake') return a.platformType === 'snowflake' || a.isSnowflakeTask;
        return a.platformType === platformFilter;
      });
    }
    return list;
  }, [automations, selectedCategory, nameFilter, platformFilter]);

  // Generate scheduled events from cron expressions
  const scheduledEvents = useMemo(() => {
    return generateScheduledEvents(filteredAutomations, visibleRange.start, visibleRange.end);
  }, [filteredAutomations, visibleRange]);

  // Fetch run history when visible range includes past dates
  useEffect(() => {
    const now = new Date();
    if (visibleRange.start >= now) {
      setHistoryEvents([]);
      return;
    }

    const fetchHistory = async () => {
      if (!firebaseUser?.getIdToken) return;
      try {
        setHistoryLoading(true);
        const token = await firebaseUser.getIdToken(true);
        const startDate = visibleRange.start.toISOString().split('T')[0];
        const endDate = (visibleRange.end < now ? visibleRange.end : now).toISOString().split('T')[0];

        const res = await axios.get('/hd-api/data-integrity-2/automations/run-history', {
          headers: { Authorization: `Bearer ${token}` },
          params: { startDate, endDate },
        });

        const records = res.data.history || [];
        setHistoryEvents(mapHistoryToEvents(records, automationsMap));
      } catch (err) {
        console.warn('Failed to fetch run history for calendar:', err.message);
        setHistoryEvents([]);
      } finally {
        setHistoryLoading(false);
      }
    };

    fetchHistory();
  }, [visibleRange.start.getTime(), visibleRange.end.getTime(), firebaseUser]);

  // Deduplicate: if a history event exists at the same time as a scheduled event, keep history
  const allEvents = useMemo(() => {
    const historyKeys = new Set();
    historyEvents.forEach((e) => {
      // Key by automationId + hour (to match scheduled events within the same hour)
      const hourKey = `${e.automationId}-${e.start.getFullYear()}-${e.start.getMonth()}-${e.start.getDate()}-${e.start.getHours()}`;
      historyKeys.add(hourKey);
    });

    const dedupedScheduled = scheduledEvents.filter((e) => {
      const hourKey = `${e.automationId}-${e.start.getFullYear()}-${e.start.getMonth()}-${e.start.getDate()}-${e.start.getHours()}`;
      return !historyKeys.has(hourKey);
    });

    // Filter history events by the same filters
    const filteredIds = new Set(filteredAutomations.map((a) => a.id));
    const filteredHistory = historyEvents.filter((e) => filteredIds.has(e.automationId));

    return collapseFrequentEvents([...filteredHistory, ...dedupedScheduled]);
  }, [scheduledEvents, historyEvents, filteredAutomations]);

  const handleSelectEvent = useCallback((event, e) => {
    setSelectedEvent(event);
    setPopoverAnchor(e.currentTarget || e.target);
  }, []);

  const handleClosePopover = () => {
    setPopoverAnchor(null);
    setSelectedEvent(null);
  };

  const handleNavigate = useCallback((newDate) => setCurrentDate(newDate), []);
  const handleViewChange = useCallback((newView) => setView(newView), []);

  return (
    <Box className="automations-calendar" sx={{ px: 2, pb: 2, pt: 1 }}>
      {historyLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Loading run history...
          </Typography>
        </Box>
      )}

      <Calendar
        localizer={localizer}
        events={allEvents}
        date={currentDate}
        view={view}
        onNavigate={handleNavigate}
        onView={handleViewChange}
        onSelectEvent={handleSelectEvent}
        eventPropGetter={getEventStyle}
        formats={{
          eventTimeRangeFormat: () => '',
        }}
        components={{
          toolbar: CalendarToolbar,
          event: CalendarEvent,
        }}
        style={{ height: 'calc(100vh - 420px)', minHeight: 500 }}
        popup
        views={['month', 'week', 'day']}
      />

      <CalendarLegend />

      {/* Event Detail Popover */}
      <Popover
        open={!!popoverAnchor}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {selectedEvent && (
          <Box sx={{ p: 2, maxWidth: 340 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: (CATEGORY_COLORS[selectedEvent.categoryKey] || CATEGORY_COLORS.other).main,
                }}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'Lato' }}>
                {shortenAutomationName(selectedEvent.title)}
              </Typography>
            </Box>

            {/* Show full qualified name if it differs */}
            {selectedEvent.title !== shortenAutomationName(selectedEvent.title) && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5, wordBreak: 'break-all' }}>
                {selectedEvent.title}
              </Typography>
            )}

            {selectedEvent.collapsed ? (
              <>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                  <strong>Runs today:</strong> {selectedEvent.collapsedCount}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  {format(selectedEvent.start, 'p')} – {format(selectedEvent.end, 'p')}
                </Typography>
                {selectedEvent.collapsedEvents.some((e) => e.status === 'failed') && (
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'error.main' }}>
                    <strong>{selectedEvent.collapsedEvents.filter((e) => e.status === 'failed').length} failed</strong> run(s)
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                {format(selectedEvent.start, 'PPpp')}
              </Typography>
            )}

            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
              <strong>Status:</strong> {(STATUS_COLORS[selectedEvent.status] || STATUS_COLORS.scheduled).label}
            </Typography>

            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
              <strong>Category:</strong>{' '}
              <span style={{ textTransform: 'capitalize' }}>{selectedEvent.categoryKey}</span>
            </Typography>

            {selectedEvent.automation?.scheduleCron && (
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                <strong>Schedule:</strong> {selectedEvent.automation.scheduleCron}
              </Typography>
            )}

            {selectedEvent.automation?.scheduleTimezone && (
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                <strong>Timezone:</strong> {selectedEvent.automation.scheduleTimezone}
              </Typography>
            )}

            {selectedEvent.errorMessage && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.5, color: 'error.main', wordBreak: 'break-word' }}
              >
                <strong>Error:</strong> {selectedEvent.errorMessage}
              </Typography>
            )}
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default AutomationsCalendar;
